import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import Modal from 'react-native-modal/dist/modal';
import { useTheme } from '~contexts/ThemeContext';
import { useSubscriptionContext } from '~contexts/SubscriptionContext';
import { useGetPyxiSelectEvents } from '~hooks/event/useGetPyxiSelectEvents';
import { useTransferBooking } from '~hooks/event/useTransferBooking';
import { useCreateTransferPaymentIntent } from '~hooks/event/useCreateTransferPaymentIntent';
import { useGetEventById } from '~hooks/event/useGetEventById';
import { useGetEventUserStatus } from '~hooks/event/useGetEventUserStatus';
import { useGetOrderById } from '~hooks/event/useGetOrderById';
import { useCheckTransferEligibility } from '~hooks/order/useCheckTransferEligibility';
import { Event } from '~types/api/event';
import moment from 'moment';
import { Notifier, NotifierComponents } from 'react-native-notifier';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useStripe } from '@stripe/stripe-react-native';

interface TransferBookingModalProps {
  visible: boolean;
  onClose: () => void;
  currentEventId: string;
  userEmail: string;
  userId: string;
  userName: string;
  onTransferSuccess?: () => void;
}

const TransferBookingModal: React.FC<TransferBookingModalProps> = ({
  visible,
  onClose,
  currentEventId,
  userEmail,
  userId,
  userName,
  onTransferSuccess,
}) => {
  const { colors } = useTheme();
  const { activePlan } = useSubscriptionContext();
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  
  const { data: availableEvents, isLoading: eventsLoading, error: eventsError } = useGetPyxiSelectEvents(currentEventId);
  const { data: currentEvent } = useGetEventById(parseInt(currentEventId));
  const { data: userStatus } = useGetEventUserStatus({ event_id: parseInt(currentEventId), user_id: userId });
  const { data: userOrder } = useGetOrderById(userStatus?.order_id || '');
  const { data: transferEligibility, isLoading: eligibilityLoading, error: eligibilityError } = useCheckTransferEligibility(userStatus?.order_id || '');
  const { mutate: transferBooking, isLoading: isTransferring } = useTransferBooking();
  const { mutateAsync: createTransferPaymentIntent } = useCreateTransferPaymentIntent();
  const { initPaymentSheet, presentPaymentSheet } = useStripe();

  const styles = StyleSheet.create({
    modal: {
      justifyContent: 'flex-end',
      margin: 0,
    },
    modalContent: {
      backgroundColor: colors.white,
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      paddingTop: 20,
      paddingHorizontal: 20,
      paddingBottom: 40,
      maxHeight: '80%',
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 20,
      paddingBottom: 15,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    title: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.textPrimary,
    },
    closeButton: {
      padding: 5,
    },
    userInfo: {
      backgroundColor: colors.surface,
      padding: 15,
      borderRadius: 12,
      marginBottom: 20,
    },
    userInfoText: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 5,
    },
    userName: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.textPrimary,
      marginBottom: 5,
    },
    userEmail: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    currentEventInfo: {
      marginTop: 10,
      paddingTop: 10,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    currentEventLabel: {
      fontSize: 12,
      color: colors.textSecondary,
      marginBottom: 3,
    },
    currentEventPrice: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.primary,
    },
    sectionTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.textPrimary,
      marginBottom: 15,
    },
    eventItem: {
      backgroundColor: colors.surface,
      padding: 15,
      borderRadius: 12,
      marginBottom: 10,
      borderWidth: 2,
      borderColor: 'transparent',
    },
    selectedEventItem: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '10',
    },
    eventName: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.textPrimary,
      marginBottom: 5,
    },
    eventDate: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 3,
    },
    eventLocation: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    priceContainer: {
      marginTop: 5,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    priceInfo: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    currentPrice: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.textPrimary,
    },
    freePrice: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.statusGreen,
    },
    extraPaymentContainer: {
      backgroundColor: colors.warning + '20',
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: colors.warning + '40',
    },
    extraPaymentText: {
      fontSize: 12,
      fontWeight: '600',
      color: colors.warning,
    },
    cheaperContainer: {
      backgroundColor: colors.statusGreen + '20',
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: colors.statusGreen + '40',
    },
    cheaperText: {
      fontSize: 12,
      fontWeight: '600',
      color: colors.statusGreen,
    },
    samePriceContainer: {
      backgroundColor: colors.primary + '20',
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: colors.primary + '40',
    },
    samePriceText: {
      fontSize: 12,
      fontWeight: '600',
      color: colors.primary,
    },
    showMoreButton: {
      backgroundColor: colors.gray100,
      paddingVertical: 12,
      paddingHorizontal: 20,
      borderRadius: 8,
      marginTop: 10,
      alignItems: 'center',
    },
    showMoreText: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.primary,
    },
    loadingContainer: {
      padding: 40,
      alignItems: 'center',
    },
    loadingText: {
      fontSize: 16,
      color: colors.textSecondary,
      marginTop: 10,
    },
    errorContainer: {
      padding: 20,
      alignItems: 'center',
    },
    errorText: {
      fontSize: 16,
      color: colors.error,
      textAlign: 'center',
    },
    retryButton: {
      backgroundColor: colors.primary,
      paddingHorizontal: 20,
      paddingVertical: 10,
      borderRadius: 8,
      marginTop: 15,
    },
    retryButtonText: {
      color: colors.white,
      fontWeight: '600',
    },
    infoBox: {
      backgroundColor: colors.warning + '20',
      padding: 15,
      borderRadius: 12,
      marginBottom: 20,
      borderLeftWidth: 4,
      borderLeftColor: colors.warning,
    },
    infoText: {
      fontSize: 14,
      color: colors.textPrimary,
      lineHeight: 20,
    },
    transferLimitContainer: {
      backgroundColor: colors.error + '08',
      padding: 24,
      borderRadius: 16,
      marginBottom: 20,
      borderWidth: 2,
      borderColor: colors.error + '20',
      alignItems: 'center',
      shadowColor: colors.error,
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    },
    transferLimitTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.error,
      marginBottom: 12,
      textAlign: 'center',
    },
    transferLimitText: {
      fontSize: 15,
      color: colors.textPrimary,
      lineHeight: 22,
      textAlign: 'center',
      marginBottom: 8,
      opacity: 0.8,
    },
    transferStatsContainer: {
      backgroundColor: colors.white,
      padding: 0,
      borderRadius: 12,
      marginTop: 15,
      borderWidth: 1,
      borderColor: colors.border,
      overflow: 'hidden',
    },
    transferStatItem: {
      backgroundColor: colors.surface,
      paddingVertical: 16,
      paddingHorizontal: 20,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      alignItems: 'center',
    },
    transferStatItemLast: {
      borderBottomWidth: 0,
    },
    transferStatValue: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.textPrimary,
      marginBottom: 4,
    },
    transferStatLabel: {
      fontSize: 12,
      color: colors.textSecondary,
      textAlign: 'center',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    transferStatsRow: {
      flexDirection: 'row',
      borderRadius: 12,
      overflow: 'hidden',
      backgroundColor: colors.white,
      borderWidth: 1,
      borderColor: colors.border,
    },
    transferStatCard: {
      flex: 1,
      backgroundColor: colors.surface,
      paddingVertical: 20,
      paddingHorizontal: 15,
      alignItems: 'center',
      borderRightWidth: 1,
      borderRightColor: colors.border,
    },
    transferStatCardLast: {
      borderRightWidth: 0,
    },
    transferStatCardUsed: {
      backgroundColor: colors.error + '15',
    },
    transferStatCardMax: {
      backgroundColor: colors.warning + '15',
    },
    transferStatCardRemaining: {
      backgroundColor: colors.statusGreen + '15',
    },
    transferStatValueUsed: {
      fontSize: 28,
      fontWeight: 'bold',
      color: colors.error,
      marginBottom: 6,
    },
    transferStatValueMax: {
      fontSize: 28,
      fontWeight: 'bold',
      color: colors.warning,
      marginBottom: 6,
    },
    transferStatValueRemaining: {
      fontSize: 28,
      fontWeight: 'bold',
      color: colors.statusGreen,
      marginBottom: 6,
    },
    transferStatLabelCard: {
      fontSize: 11,
      color: colors.textSecondary,
      textAlign: 'center',
      textTransform: 'uppercase',
      letterSpacing: 0.8,
      fontWeight: '600',
    },
    buttonContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginTop: 20,
    },
    button: {
      flex: 1,
      paddingVertical: 15,
      borderRadius: 12,
      alignItems: 'center',
      marginHorizontal: 5,
    },
    cancelButton: {
      backgroundColor: colors.gray200,
    },
    transferButton: {
      backgroundColor: colors.warning,
    },
    disabledButton: {
      backgroundColor: colors.gray300,
    },
    buttonText: {
      fontSize: 16,
      fontWeight: '600',
    },
    cancelButtonText: {
      color: colors.textPrimary,
    },
    transferButtonText: {
      color: colors.white,
    },
    disabledButtonText: {
      color: colors.textSecondary,
    },
  });

  const handleTransfer = async () => {
    if (!selectedEventId) {
      Alert.alert('Selection Required', 'Please select an event to transfer to.');
      return;
    }

    const selectedEvent = availableEvents?.find(event => String(event.event_id) === selectedEventId);
    if (!selectedEvent) {
      Alert.alert('Error', 'Selected event not found.');
      return;
    }

    const targetEventPrice = getMinEventPrice(selectedEvent);
    
    // Check if payment is needed based on user's actual order
    const needsPayment = isPaymentRequired();
    const extraAmount = getExtraPaymentAmount();
    
    // Create price information for confirmation
    let priceMessage = '';
    if (needsPayment && userOrder && targetEventPrice) {
      priceMessage = `\n\nNote: You will be charged an additional ${formatCurrency(extraAmount, targetEventPrice.currency)} for the price difference (${userOrder.total_tickets} tickets).`;
    } else if (userOrder) {
      priceMessage = `\n\nNote: No additional payment required for transferring ${userOrder.total_tickets} tickets.`;
    }

    Alert.alert(
      'Confirm Transfer',
      `Are you sure you want to transfer ${userName} to "${selectedEvent.name}"? This action cannot be undone.${priceMessage}`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: needsPayment ? 'Pay & Transfer' : 'Transfer',
          style: 'destructive',
          onPress: async () => {
            if (needsPayment) {
              // Payment required - use transfer payment intent API
              try {
                const transferData = {
                  source_event_id: currentEventId,
                  target_event_id: selectedEventId,
                  user_id: userId,
                  email: userEmail,
                };

                const paymentData = await createTransferPaymentIntent({
                  eventId: currentEventId,
                  transferData,
                });

                // Initialize payment sheet
                const isInitialized = await initializePaymentSheet(paymentData.client_secret);
                
                if (isInitialized) {
                  // Present payment sheet
                  await handlePaymentAndTransfer(selectedEvent);
                } else {
                  Alert.alert('Error', 'Failed to initialize payment. Please try again.');
                }
              } catch (error: any) {
                console.error('Transfer payment error:', error);
                Alert.alert(
                  'Payment Error', 
                  error.message || 'Failed to process payment. Please try again.'
                );
              }
            } else {
              // No payment needed - direct transfer
              transferBooking(
                {
                  eventId: currentEventId,
                  transferData: {
                    user_id: userId,
                    email: userEmail,
                    target_event_id: selectedEventId,
                  },
                },
                {
                  onSuccess: () => {
                    Notifier.showNotification({
                      title: 'Transfer Successful',
                      description: `${userName} has been successfully transferred to the new event.`,
                      Component: NotifierComponents.Alert,
                      componentProps: {
                        alertType: 'success',
                      },
                    });
                    onTransferSuccess?.();
                    onClose();
                  },
                  onError: (error) => {
                    Notifier.showNotification({
                      title: 'Transfer Failed',
                      description: error.message || 'Failed to transfer booking. Please try again.',
                      Component: NotifierComponents.Alert,
                      componentProps: {
                        alertType: 'error',
                      },
                    });
                  },
                }
              );
            }
          },
        },
      ]
    );
  };

  const [showAll, setShowAll] = useState(false);

  // Initialize Stripe payment sheet
  const initializePaymentSheet = async (clientSecret: string) => {
    const { error } = await initPaymentSheet({
      merchantDisplayName: 'Pyxi',
      paymentIntentClientSecret: clientSecret,
      returnURL: 'https://partner.pyxi.ai/payment-return',
    });

    if (error) {
      console.error('Error initializing Payment Sheet:', error);
      return false;
    }

    console.log('Payment Sheet initialized successfully');
    return true;
  };

  // Present payment sheet and handle transfer
  const handlePaymentAndTransfer = async (selectedEvent: Event) => {
    const { error } = await presentPaymentSheet();

    if (error) {
      Notifier.showNotification({
        title: 'Payment Failed',
        description: error.message || 'Payment was cancelled or failed.',
        Component: NotifierComponents.Alert,
        componentProps: {
          alertType: 'error',
        },
      });
      console.error('Error presenting Payment Sheet:', error.message);
    } else {
      // Payment successful - backend automatically handles the transfer
      Notifier.showNotification({
        title: 'Transfer Successful',
        description: `${userName} has been successfully transferred to "${selectedEvent.name}" and payment processed.`,
        Component: NotifierComponents.Alert,
        componentProps: {
          alertType: 'success',
        },
      });
      onTransferSuccess?.();
      onClose();
    }
  };

  // Helper function to get the minimum ticket price for an event
  const getMinEventPrice = (event: Event): { price: number; currency: string } | null => {
    if (!event.is_paid || !event.tickets || event.tickets.length === 0) {
      return null;
    }
    
    const validTickets = event.tickets.filter(ticket => !ticket.removed);
    if (validTickets.length === 0) {
      return null;
    }
    
    const minTicket = validTickets.reduce((min, ticket) => {
      const ticketPrice = ticket.discounted_price > 0 ? ticket.discounted_price : ticket.price;
      const minPrice = min.discounted_price > 0 ? min.discounted_price : min.price;
      return ticketPrice < minPrice ? ticket : min;
    });
    
    const price = minTicket.discounted_price > 0 ? minTicket.discounted_price : minTicket.price;
    return { price, currency: minTicket.currency || '$' };
  };

  // Helper function to format currency display
  const formatCurrency = (amount: number, currency: string): string => {
    const CURRENCY_SYMBOLS: Record<string, string> = {
      GBP: '£',
      USD: '$',
      EUR: '€',
      CAD: '$',
      AUD: '$',
      NZD: '$',
      JPY: '¥',
      CHF: 'CHF',
      CNY: '¥',
      INR: '₹',
      KRW: '₩',
      SGD: '$',
      HKD: '$',
      NOK: 'kr',
      SEK: 'kr',
      DKK: 'kr',
      PLN: 'zł',
      CZK: 'Kč',
      HUF: 'Ft',
      MYR: 'RM',
      THB: '฿',
      IDR: 'Rp',
      PHP: '₱',
      VND: '₫',
      TRY: '₺',
      ZAR: 'R',
      BRL: 'R$',
      MXN: '$',
      ARS: '$',
      CLP: '$',
      COP: '$',
      PEN: 'S/',
    };

    const symbol = CURRENCY_SYMBOLS[currency.toUpperCase()] || currency;
    const needsSpace = /^(CHF|kr|zł|Kč|Ft|S\/|RM|Rp)$/.test(symbol);
    
    return needsSpace ? `${symbol} ${amount.toFixed(2)}` : `${symbol}${amount.toFixed(2)}`;
  };

  // Get current event price info
  const currentEventPrice = currentEvent ? getMinEventPrice(currentEvent) : null;

  // Helper function to determine if payment is required
  const isPaymentRequired = () => {
    if (!selectedEventId || !userOrder) return false;
    
    const selectedEvent = availableEvents?.find(event => String(event.event_id) === selectedEventId);
    if (!selectedEvent) return false;
    
    // Check if target event is covered by subscription
    if (activePlan && activePlan.plan?.feature_list) {
      const featureList = activePlan.plan.feature_list;
      const eventType = selectedEvent.event_service_type;
      
      switch (eventType) {
        case 'THE_REST_TABLE':
          if (featureList.unlimited_dinners === true) return false;
          break;
        case 'CULTURAL_EDIT':
          if (featureList.culture_edit_events === true) return false;
          break;
        case 'HOUSEWARMING':
          if (featureList.monthly_pyxi_party && featureList.monthly_pyxi_party > 0) return false;
          break;
      }
    }
    
    const targetEventPrice = getMinEventPrice(selectedEvent);
    if (!targetEventPrice) return false;
    
    // Calculate total cost for same number of tickets at target event
    const targetEventTotalCost = targetEventPrice.price * userOrder.total_tickets;
    
    // Payment needed if target event total cost is more than what user paid
    return targetEventTotalCost > userOrder.total_amount;
  };

  // Helper function to calculate extra payment amount
  const getExtraPaymentAmount = () => {
    if (!selectedEventId || !userOrder) return 0;

    console.log(activePlan, 'activePlanactivePlan');
    
    
    const selectedEvent = availableEvents?.find(event => String(event.event_id) === selectedEventId);
    if (!selectedEvent) return 0;
    
    // Check if target event is covered by subscription
    if (activePlan && activePlan.plan?.feature_list) {
      const featureList = activePlan.plan.feature_list;
      const eventType = selectedEvent.event_service_type;
      
      switch (eventType) {
        case 'THE_REST_TABLE':
          if (featureList.unlimited_dinners === true) return 0;
          break;
        case 'CULTURAL_EDIT':
          if (featureList.culture_edit_events === true) return 0;
          break;
        case 'HOUSEWARMING':
          if (featureList.monthly_pyxi_party && featureList.monthly_pyxi_party > 0) return 0;
          break;
      }
    }
    
    const targetEventPrice = getMinEventPrice(selectedEvent);
    if (!targetEventPrice) return 0;
    
    // Calculate total cost for same number of tickets at target event
    const targetEventTotalCost = targetEventPrice.price * userOrder.total_tickets;
    
    // Return extra amount needed (difference between target cost and what user paid)
    return Math.max(0, targetEventTotalCost - userOrder.total_amount);
  };

const MAX_VISIBLE_EVENTS = 30;

// Filtered events excluding the current one
const filteredEvents =
  availableEvents?.filter((event) => String(event.event_id) !== currentEventId) || [];

// Conditionally show limited or all events
const displayedEvents = showAll
  ? filteredEvents
  : filteredEvents.slice(0, MAX_VISIBLE_EVENTS);

  const renderEventItem = (event: Event) => {
    const isSelected = selectedEventId === String(event.event_id);
    const targetEventPrice = getMinEventPrice(event);
    
    // Check if target event is covered by subscription
    let isCoveredBySubscription = false;
    if (activePlan && activePlan.plan?.feature_list) {
      const featureList = activePlan.plan.feature_list;
      const eventType = event.event_service_type;
      
      switch (eventType) {
        case 'THE_REST_TABLE':
          isCoveredBySubscription = featureList.unlimited_dinners === true;
          break;
        case 'CULTURAL_EDIT':
          isCoveredBySubscription = featureList.culture_edit_events === true;
          break;
        case 'HOUSEWARMING':
          isCoveredBySubscription = (featureList.monthly_pyxi_party && featureList.monthly_pyxi_party > 0) as boolean;
          break;
      }
    }
    
    // Check if extra payment is needed based on user's actual order
    let needsExtraPayment = false;
    let extraAmount = 0;
    
    // If event is covered by subscription, no extra payment needed
    if (!isCoveredBySubscription && userOrder && targetEventPrice) {
      const targetEventTotalCost = targetEventPrice.price * userOrder.total_tickets;
      needsExtraPayment = targetEventTotalCost > userOrder.total_amount;
      extraAmount = Math.max(0, targetEventTotalCost - userOrder.total_amount);
    }
    
    return (
      <TouchableOpacity
        key={event.event_id}
        style={[styles.eventItem, isSelected && styles.selectedEventItem]}
        onPress={() => setSelectedEventId(String(event.event_id))}
        activeOpacity={0.7}
      >
        <Text style={styles.eventName}>{event.name}</Text>
        <Text style={styles.eventDate}>
          {moment(event.start_date).format('MMM DD, YYYY h:mm A')}
        </Text>
        <Text style={styles.eventLocation}>
          {event.event_service_type === 'THE_REST_TABLE' ? event.address_name : event.coord_address}
        </Text>
        
        {/* Only show payment message when extra payment is needed */}
        {needsExtraPayment && targetEventPrice && (
          <View style={styles.priceContainer}>
            <View style={styles.extraPaymentContainer}>
              <Text style={styles.extraPaymentText}>
                Extra payment required: {formatCurrency(extraAmount, targetEventPrice.currency)}
              </Text>
            </View>
          </View>
        )}
      </TouchableOpacity>
    );
  };

  return (
    <Modal
      isVisible={visible}
      onBackdropPress={onClose}
      style={styles.modal}
      animationIn="slideInUp"
      animationOut="slideOutDown"
    >
      
        <View style={styles.modalContent}>
          <View style={styles.header}>
            <Text style={styles.title}>Transfer Booking</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <MaterialIcons name="close" size={24} color={colors.textPrimary} />
            </TouchableOpacity>
          </View>

          <ScrollView showsVerticalScrollIndicator={false}>
            <View style={styles.userInfo}>
              <Text style={styles.userInfoText}>Transferring booking for:</Text>
              <Text style={styles.userName}>{userName}</Text>
              <Text style={styles.userEmail}>{userEmail}</Text>
            </View>

            {/* Transfer Eligibility Check */}
            {eligibilityLoading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
                <Text style={styles.loadingText}>Checking transfer eligibility...</Text>
              </View>
            ) : eligibilityError ? (
              <View style={styles.errorContainer}>
                <MaterialIcons name="error-outline" size={48} color={colors.error} />
                <Text style={styles.errorText}>
                  Failed to check transfer eligibility. Please try again.
                </Text>
              </View>
            ) : transferEligibility && !transferEligibility.can_transfer ? (
              <View style={styles.transferLimitContainer}>
                <View style={{
                  backgroundColor: colors.error + '15',
                  borderRadius: 30,
                  padding: 8,
                  marginBottom: 12,
                }}>
                  <MaterialIcons 
                    name="block" 
                    size={40} 
                    color={colors.error} 
                  />
                </View>
                <Text style={styles.transferLimitTitle}>Transfer Limit Reached</Text>
                <Text style={styles.transferLimitText}>
                  This booking cannot be transferred as the maximum number of transfers allowed has been reached.
                </Text>
                
                <View style={styles.transferStatsRow}>
                  <View style={[styles.transferStatCard, styles.transferStatCardUsed]}>
                    <Text style={styles.transferStatValueUsed}>
                      {transferEligibility.current_transfer_count}
                    </Text>
                    <Text style={styles.transferStatLabelCard}>Used</Text>
                  </View>
                  
                  <View style={[styles.transferStatCard, styles.transferStatCardMax]}>
                    <Text style={styles.transferStatValueMax}>
                      {transferEligibility.max_transfers_allowed}
                    </Text>
                    <Text style={styles.transferStatLabelCard}>Max Allowed</Text>
                  </View>
                  
                  <View style={[styles.transferStatCard, styles.transferStatCardRemaining, styles.transferStatCardLast]}>
                    <Text style={styles.transferStatValueRemaining}>
                      {transferEligibility.remaining_transfers}
                    </Text>
                    <Text style={styles.transferStatLabelCard}>Remaining</Text>
                  </View>
                </View>
              </View>
            ) : (
              <>
                <View style={styles.infoBox}>
                  <Text style={styles.infoText}>
                    ℹ️ The user will be transferred to the selected event. Their current registration status will be maintained. If the new event is more expensive, payment will be processed automatically.
                  </Text>
                </View>

                <Text style={styles.sectionTitle}>Select Event</Text>

                {eventsLoading ? (
                  <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color={colors.primary} />
                    <Text style={styles.loadingText}>Loading available events...</Text>
                  </View>
                ) : eventsError ? (
                  <View style={styles.errorContainer}>
                    <MaterialIcons name="error-outline" size={48} color={colors.error} />
                    <Text style={styles.errorText}>
                      Failed to load available events. Please try again.
                    </Text>
                    <TouchableOpacity style={styles.retryButton}>
                      <Text style={styles.retryButtonText}>Retry</Text>
                    </TouchableOpacity>
                  </View>
                ) : filteredEvents.length === 0 ? (
                  <View style={styles.errorContainer}>
                    <MaterialIcons name="event-busy" size={48} color={colors.textSecondary} />
                    <Text style={styles.errorText}>
                      No other events available for transfer at this time.
                    </Text>
                  </View>
                ) : (
                  <>
                    {displayedEvents.map(renderEventItem)}
                    
                    {/* Show more button if there are more events */}
                    {!showAll && filteredEvents.length > MAX_VISIBLE_EVENTS && (
                      <TouchableOpacity 
                        style={styles.showMoreButton}
                        onPress={() => setShowAll(true)}
                      >
                        <Text style={styles.showMoreText}>
                          Show {filteredEvents.length - MAX_VISIBLE_EVENTS} more events
                        </Text>
                      </TouchableOpacity>
                    )}
                  </>
                )}
              </>
            )}
          </ScrollView>

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.button, styles.cancelButton]}
              onPress={onClose}
              disabled={isTransferring}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            
            {/* Only show transfer button if transfer is allowed */}
            {transferEligibility?.can_transfer !== false && (
              <TouchableOpacity
                style={[
                  styles.button,
                  styles.transferButton,
                  (!selectedEventId || isTransferring || eligibilityLoading) && styles.disabledButton,
                ]}
                onPress={handleTransfer}
                disabled={!selectedEventId || isTransferring || eligibilityLoading || !transferEligibility?.can_transfer}
              >
                {isTransferring ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <Text style={[
                    styles.transferButtonText,
                    (!selectedEventId || isTransferring || eligibilityLoading || !transferEligibility?.can_transfer) && styles.disabledButtonText,
                  ]}>
                    {isPaymentRequired() ? 'Pay & Transfer' : 'Transfer Booking'}
                  </Text>
                )}
              </TouchableOpacity>
            )}
          </View>
        </View>
     
    </Modal>
  );
};

export default TransferBookingModal;
